import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDate, euro } from "@/lib/format";
import { MapPin, CalendarClock, UserRound, Phone, MessageCircle, HandHeart, Info, BadgeEuro, FileText, Check } from "lucide-react";

const intlPhone = (p) => { const d = (p || "").replace(/\D/g, ""); return d.startsWith("39") ? d : `39${d}`; };

function TariffaEditor({ r, onSave }) {
  const [val, setVal] = useState(r.agreed_price_cents != null ? (r.agreed_price_cents / 100).toString() : "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (val === "" || isNaN(Number(val)) || Number(val) <= 0) return toast.error("Inserisci una tariffa maggiore di zero.");
    setSaving(true);
    try { await onSave(r.id, Math.round(Number(val) * 100)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-4 bg-white border border-[#D2E3DB] rounded-xl p-4">
      <p className="text-sm font-semibold text-[#102A2A] flex items-center gap-1 mb-1"><BadgeEuro className="h-4 w-4 text-[#087A73]" /> Tariffa concordata</p>
      <p className="text-xs text-[#5C6B68] mb-3">Indica quanto pagherà il paziente. Sarà visibile al paziente e all'amministrazione. Il pagamento avviene fuori piattaforma.</p>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="relative w-[160px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B68]">€</span>
          <Input data-testid={`tariffa-input-${r.id}`} type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} className="h-11 rounded-xl pl-7" placeholder="Es. 90" />
        </div>
        <Button data-testid={`tariffa-save-${r.id}`} onClick={save} disabled={saving} className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-11 px-5"><Check className="mr-1 h-4 w-4" /> Salva tariffa</Button>
      </div>
      {r.agreed_price_cents != null && <p data-testid={`tariffa-current-${r.id}`} className="text-sm text-[#087A73] font-bold mt-2">Tariffa attuale: {euro(r.agreed_price_cents)}</p>}
    </div>
  );
}

function RequestCard({ r, onTake, onSetPrice, taken }) {
  const wa = `https://wa.me/${intlPhone(r.contact_phone)}?text=${encodeURIComponent(`Salve, sono un medico di EtruriaCare. La contatto per la sua richiesta di ${r.service_name} a ${r.municipality}.`)}`;
  return (
    <Card data-testid={`prof-request-${r.id}`}>
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-[#102A2A]">{r.service_name}</p>
            {r.urgency === "prioritaria" && <span className="text-xs font-bold text-[#C0392B] bg-[#F9E3E0] rounded-full px-2 py-0.5">Prioritaria</span>}
          </div>
          {r.description && <p className="text-sm text-[#102A2A] flex items-start gap-1"><FileText className="h-4 w-4 mt-0.5 shrink-0 text-[#087A73]" /> <span><strong>Richiesta:</strong> {r.description}</span></p>}
          {(r.inferred_prestazioni_names || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {r.inferred_prestazioni_names.map((n) => <span key={n} className="text-xs font-semibold bg-[#E0EFEA] text-[#087A73] rounded-full px-2.5 py-0.5">{n}</span>)}
            </div>
          )}
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><UserRound className="h-4 w-4" /> Per: {r.recipient_name}</p>
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><CalendarClock className="h-4 w-4" /> Preferenza: {formatDate(r.preferred_date)} · {r.preferred_time_range}</p>
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><MapPin className="h-4 w-4" /> {r.address}, {r.municipality}</p>
          {r.logistics_note && <p className="text-sm text-[#5C6B68]">Note: {r.logistics_note}</p>}
        </div>
        <div className="text-left sm:text-right shrink-0">
          <p className="text-xs text-[#5C6B68]">Recapito paziente</p>
          <p className="font-bold text-[#102A2A]">{r.contact_phone}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {!taken && <Button data-testid={`take-${r.id}`} onClick={() => onTake(r.id)} className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-11 px-5"><HandHeart className="mr-1 h-4 w-4" /> Prendi in carico</Button>}
        <a href={`tel:${r.contact_phone}`} data-testid={`call-${r.id}`} className="inline-flex items-center rounded-xl h-11 px-5 border-2 border-[#087A73] text-[#087A73] text-sm font-semibold hover:bg-[#087A73]/10 transition-colors"><Phone className="mr-1 h-4 w-4" /> Chiama</a>
        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid={`wa-${r.id}`} className="inline-flex items-center rounded-xl h-11 px-5 bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1fb457] transition-colors"><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp</a>
      </div>
      {taken && <TariffaEditor r={r} onSave={onSetPrice} />}
    </Card>
  );
}

export default function RichiesteRicevute() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["prof-requests"], queryFn: async () => (await api.get("/professional/requests")).data });
  const open = data?.open || [];
  const mine = data?.mine || [];

  const take = async (id) => {
    try {
      await api.post(`/professional/requests/${id}/take`);
      toast.success("Richiesta presa in carico. Contatta il paziente e indica la tariffa.");
      qc.invalidateQueries({ queryKey: ["prof-requests"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const setPrice = async (id, price_cents) => {
    try {
      await api.patch(`/professional/requests/${id}/price`, { price_cents });
      toast.success("Tariffa salvata. Il paziente la vedrà nelle sue richieste.");
      qc.invalidateQueries({ queryKey: ["prof-requests"] });
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  return (
    <DashboardLayout title="Richieste ricevute">
      <div className="mb-4 flex items-start gap-2 bg-[#EAF3EF] border border-[#D2E3DB] text-[#5C6B68] rounded-xl px-4 py-3 text-sm">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#087A73]" /> Qui vedi le richieste dei pazienti per le prestazioni che offri nei tuoi comuni. Prendile in carico, contatta il paziente e indica la tariffa concordata.
      </div>
      {isLoading ? <LoadingBlock /> : (
        <div className="space-y-8">
          <div>
            <h3 className="font-bold text-[#102A2A] mb-3">Nuove richieste disponibili</h3>
            {open.length === 0 ? (
              <EmptyState title="Nessuna nuova richiesta" subtitle="Le nuove richieste dei pazienti per le tue prestazioni appariranno qui." />
            ) : (
              <div className="space-y-4" data-testid="open-requests">{open.map((r) => <RequestCard key={r.id} r={r} onTake={take} />)}</div>
            )}
          </div>
          {mine.length > 0 && (
            <div>
              <h3 className="font-bold text-[#102A2A] mb-3">Prese in carico da te</h3>
              <div className="space-y-4" data-testid="my-requests">{mine.map((r) => <RequestCard key={r.id} r={r} onSetPrice={setPrice} taken />)}</div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
